import { CHANGELOG } from '../../data/misc';
import { farmTotals } from '../../core/economy';
import { formatCount, formatDuration, formatMoney, formatRate } from '../../core/format';
import { countHens, countRoosters } from '../../core/state';
import { game } from '../controller';
import { h, type Bindings } from '../dom';
import { section, type Panel } from '../shop';

function ago(wall: number): string {
  const d = Date.now() - wall;
  if (d < 60_000) return 'just now';
  return `${formatDuration(d)} ago`;
}

export const statsPanel: Panel = {
  id: 'stats',
  label: 'Stats',
  key: () => `${game.state.activity.length}`,
  build(root: HTMLElement, b: Bindings) {
    const farm = section('📊 Your farm');
    const grid = h('div', { class: 'grid2' });
    const rows: [string, () => string][] = [
      ['Balance', () => formatMoney(game.state.money)],
      ['Earned (lifetime)', () => formatMoney(game.state.lifetimeEarned)],
      ['Passive income', () => `${formatRate(farmTotals(game.state, game.ctx()).passiveIncome)}/s`],
      ['Best passive income', () => `${formatRate(game.state.stats.peakIncome)}/s`],
      ['Hens / roosters', () => `${countHens(game.state)} / ${countRoosters(game.state)}`],
      ['Flocks', () => String(game.state.flocks.length)],
      ['Eggs laid', () => formatCount(game.state.eggsLaid)],
      ['Eggs collected', () => formatCount(game.state.eggsCollected)],
      ['Eggs lost (full nests)', () => formatCount(game.state.stats.eggsLost)],
      ['Clicks', () => formatCount(game.state.stats.clicks)],
      ['Crows', () => formatCount(game.state.stats.crows)],
      ['Golden / silver / rainbow', () => `${game.state.stats.goldenClicked} / ${game.state.stats.silverClicked} / ${game.state.stats.rainbowClicked}`],
      ['Broody hatches', () => formatCount(game.state.stats.broodyHatches)],
      ['Rotten: salvaged / missed', () => `${game.state.stats.rottenSalvaged} / ${game.state.stats.rottenExpired}`],
      ['Cockerels hatched', () => String(game.state.stats.roosterEggsHatched)],
      ['Achievements', () => String(game.state.achievements.length)],
      ['Active play (this farm)', () => formatDuration(game.state.stats.activePlaySec * 1000)],
      ['Farm started', () => new Date(game.state.createdWall).toLocaleDateString()],
    ];
    for (const [label, fn] of rows) {
      const v = h('span');
      b.add(() => { v.textContent = fn(); });
      grid.append(h('div', { class: 'kv' }, h('span', {}, label), v));
    }
    farm.append(grid);
    root.append(farm);

    const log = section('📜 Activity log', 'latest first');
    const entries = [...game.state.activity].reverse().slice(0, 60);
    if (!entries.length) log.append(h('p', { class: 'hint', text: 'Nothing yet — go collect some eggs!' }));
    for (const e of entries) {
      log.append(h('div', { class: 'kv' }, h('span', {}, `${e.title}${e.detail ? ` — ${e.detail}` : ''}`), h('span', { class: 'meta' }, ago(e.wall))));
    }
    root.append(log);

    const cl = section('🛠️ What\'s new');
    for (const c of CHANGELOG) {
      cl.append(h('div', { class: 'card' }, h('div', { class: 'body' },
        h('div', { class: 'title' }, `v${c.version} — ${c.title}`), h('div', { class: 'meta' }, c.date),
        h('ul', {}, ...c.notes.map((n) => h('li', { class: 'desc' }, n))))));
    }
    root.append(cl);
  },
};
